import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import React, { useCallback } from "react";
import { Box, HBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { StartScreenView } from "../StartScreenView";
import { S_AUTH_CHANGE_VIEW } from "./AuthSignals";
import { RegisterStep1 } from "./RegisterStep1";
import { RegisterStep2 } from "./RegisterStep2";
import { RegisterStep3 } from "./RegisterStep3";
import { RegisterStep4 } from "./RegisterStep4";
import { GD } from "src/GD";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Error } from "@ui/error/Error";
import { useMobile } from "src/hooks/useMobile";

export type RegisterStep = "reg1" | "reg2" | "reg3" | "reg4" | "regOk";
export const _TAuthRegister = {} as const;
export type TAuthRegister = typeof _TAuthRegister;
export type TRegistrationForm = {
	step1: {
		country: string;
		company_name: string;
		registration_number: string;
	};
	step2: {
		email: string;
		password: string;
		confirm_password: string;
		full_name: string;
	};
	step3: {
		email_verification_code: string;
	};
};

export const registrationForm = {
	step1: {
		country: "",
		company_name: "",
		registration_number: "",
	},
	step2: {
		email: "",
		password: "",
		confirm_password: "",
		full_name: "",
	},
	step3: {
		email_verification_code: "",
	},
};

const stepDescriptions: Record<RegisterStep, string[]> = {
	get reg1() { return [msg("getStartedWith"), msg("ourPayment"), msg("platform")]; },
	get reg2() { return [msg("noAccessYet"), msg("createAnAccount"), msg("toGetStarted")]; },
	get reg3() { return [msg("emailVerification"), msg("checkYourInbox"), msg("enterTheCode")]; },
	get reg4() { return [msg("almostDone"), msg("checkYourInfo"), msg("andSendIt")]; },
	get regOk() { return [msg("welcomeAboard"), msg("youReRegistered"), msg("signInToContinue")]; },
};

const getPreviousStep = (step: RegisterStep): RegisterStep | "login" => {
	const transitions: Record<RegisterStep, RegisterStep | "login"> = {
		reg1: "login",
		reg2: "reg1",
		reg3: "reg2",
		reg4: "reg3",
		regOk: "reg4",
	};
	return transitions[step] ?? "login";
};

const Description = ({ lines }: { lines: string[] }) => { useLocale(); return ((
	<Typo capitalize sx={{ fontSize: 26, fontWeight: 400 }}>
		{lines.map((line, i) => (
			<React.Fragment key={i}>
				{line}
				<br />
			</React.Fragment>
		))}
	</Typo>
)); };

export const RegisterView = ({ step }: { step: RegisterStep }) => {
	const localeRevision = useLocale();
	const errorData = GD.REG_STEP_ERROR.use();
	const isLoading = GD.REQ_STEP_BUSY.use();
	const { isMobile, isTablet } = useMobile();

	const stepComponents = React.useMemo<Record<RegisterStep, React.ReactNode>>(
		() => ({
			reg1: <RegisterStep1 />,
			reg2: <RegisterStep2 />,
			reg3: <RegisterStep3 />,
			reg4: <RegisterStep4 />,
			regOk: null,
		}),
		[],
	);

	const handleSubmit = useCallback(async () => {
		GD.REQ_STEP_BUSY.invoke(true);
		const response = await GD.REQ_AUTH_REGISTER.request(registrationForm);
		GD.REQ_STEP_BUSY.invoke(false);

		if (response?.data) {
			S_AUTH_CHANGE_VIEW.invoke("regOk");
		} else {
			GD.REG_STEP_ERROR.invoke({
				step,
				error: response?.error ?? {
					code: -1,
					message: msg("unknownError"),
				},
			});
		}
	}, [localeRevision]);

	return (
		<StartScreenView busy={isLoading || false}>
			<Box
				sx={{
					width: isMobile ? "100%" : undefined,
					overflow: isMobile ? "auto" : undefined,
					display: "flex",
					height: "100%",
					minHeight: isMobile ? undefined : "700px",
					borderStartStartRadius: isMobile ? "0" : "12px",
					borderEndStartRadius: isMobile ? "0" : "12px",
					borderEndEndRadius: isTablet ? "12px" : "0",
					borderStartEndRadius: isTablet ? "12px" : "0",
					backgroundColor: "rgba(255,255,255,1)",
				}}>
				<Box
					sx={{
						padding: isMobile ? "34px 26px" : "60px",
						minWidth: isMobile ? "0" : "500px",
						width: isMobile ? "100%" : "auto",
						display: "flex",
						margin: isMobile ? "auto" : undefined,
						maxWidth: isMobile ? "500px" : undefined,
						flexDirection: "column",
					}}>
					{errorData && (
						<>
							<Error error={errorData.error} />
							<Spacer size="20px" />
						</>
					)}
					{step === "regOk" && (
						<Box sx={{ marginBottom: 4, width: "100%", textAlign: "center" }}>
							<DotLottieReact src="/img/lottie.json" autoplay />
						</Box>
					)}

					<Description lines={stepDescriptions[step]} />

					<Spacer size="40px" />

					<Box sx={{ width: "100%", maxWidth: 500 }}>{stepComponents[step]}</Box>

					{step === "reg4" && (
						<Box sx={{ width: "100%", maxWidth: 500 }}>
							<Button onClick={handleSubmit} sx={{ width: "100%", marginTop: "32px" }}>{msg("confirm")}</Button>
						</Box>
					)}
					<>
						<Box
							sx={{
								display: "flex",
								gap: "5px",
								alignItems: "center",
								margin: "25px 0",
							}}>
							<Box
								sx={{
									border: "1px solid rgba(0,0,0,0.14)",
									width: "100%",
								}}
							/>
							<TypoCaption italic>{msg("or")}</TypoCaption>
							<Box
								sx={{
									border: "1px solid rgba(0,0,0,0.14)",
									width: "100%",
								}}
							/>
						</Box>

						<HBox gap="12px" sx={{ width: "100%" }}>
							<Button
								variant="secondary"
								onClick={() => S_AUTH_CHANGE_VIEW.invoke(getPreviousStep(step))}
								sx={{
									flex: 1,
									width: "auto",
								}}>{msg("back")}</Button>
							<Button
								variant="secondary"
								onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")}
								sx={{
									flex: 1,
									width: "auto",
								}}>{msg("signIn")}</Button>
						</HBox>
					</>
				</Box>
			</Box>
		</StartScreenView>
	);
};
